import { memo, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { IScheduleEvent } from '~/utils/interface/schedule';
import styles from './TeamCalendarGrid.module.scss';
import classNames from 'classnames/bind';

dayjs.extend(isSameOrBefore);

const cx = classNames.bind(styles);

interface TeamMember {
  id: number;
  name: string;
  color: string;
  avatar?: string | null;
}

interface TeamCalendarGridProps {
  teamMembers: TeamMember[];
  events: IScheduleEvent[];
  startDate: string; // YYYY-MM-DD format
  onEventClick: (event: IScheduleEvent) => void;
}

const TeamCalendarGrid = (props: TeamCalendarGridProps) => {
  const { teamMembers, events, startDate, onEventClick } = props;

  // Generate 7 days starting from startDate
  const weekDays = useMemo(() => {
    const start = dayjs(startDate);
    return Array.from({ length: 7 }, (_, i) => start.add(i, 'day'));
  }, [startDate]);

  // Group events by team member and date
  const eventsByMemberAndDate = useMemo(() => {
    const grouped: Record<number, Record<string, IScheduleEvent[]>> = {};

    teamMembers.forEach(member => {
      grouped[member.id] = {};
      weekDays.forEach(day => {
        grouped[member.id][day.format('YYYY-MM-DD')] = [];
      });
    });

    events.forEach(event => {
      if (!event.assignees || event.assignees.length === 0) return;

      const assigneeId = Number(event.assignees[0].id);
      const eventDate = dayjs(event.startAt).format('YYYY-MM-DD');

      if (grouped[assigneeId] && grouped[assigneeId][eventDate]) {
        grouped[assigneeId][eventDate].push(event);
      }
    });

    return grouped;
  }, [teamMembers, events, weekDays]);

  return (
    <Box className={cx('team-calendar-grid')}>
      {/* Header Row */}
      <Box className={cx('grid-header')}>
        <Box className={cx('header-cell', 'member-column')}>
          <Typography className={cx('header-text')}>Team Member</Typography>
        </Box>
        {weekDays.map((day, index) => (
          <Box key={index} className={cx('header-cell', 'day-column')}>
            <Typography className={cx('day-name')}>{day.format('ddd')}</Typography>
            <Typography className={cx('day-number')}>{day.format('D')}</Typography>
          </Box>
        ))}
      </Box>

      {/* Team Member Rows */}
      {teamMembers.map((member) => (
        <Box key={member.id} className={cx('grid-row')}>
          {/* Team Member Name Cell */}
          <Box className={cx('member-cell')}>
            <Typography className={cx('member-name')}>{member.name}</Typography>
          </Box>

          {/* Event Cells for each day */}
          {weekDays.map((day, dayIndex) => {
            const dateKey = day.format('YYYY-MM-DD');
            const dayEvents = eventsByMemberAndDate[member.id]?.[dateKey] || [];

            return (
              <Box key={dayIndex} className={cx('event-cell')}>
                {dayEvents.map((event, eventIndex) => (
                  <Box
                    key={eventIndex}
                    className={cx('event-block')}
                    sx={{
                      backgroundColor: event.assigneeColor || member.color,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onEventClick(event);
                    }}
                  >
                    <Typography className={cx('event-title')}>{event.name}</Typography>
                    <Typography className={cx('event-time')}>
                      {dayjs(event.startTime, 'HH:mm:ssZ').format('h:mm A')} -{' '}
                      {dayjs(event.endTime, 'HH:mm:ssZ').format('h:mm A')}
                    </Typography>
                  </Box>
                ))}
              </Box>
            );
          })}
        </Box>
      ))}
    </Box>
  );
};

export default memo(TeamCalendarGrid);
